import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Search, DollarSign, MapPin, FileText, Mail } from 'lucide-react';
import ComparisonView from './ComparisonView';
import PaymentModal from './PaymentModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EstimateForm({ token, insurance }) {
  const [isLoading, setIsLoading] = useState(false);
  const [procedureInput, setProcedureInput] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [estimate, setEstimate] = useState(null);
  const [comparisons, setComparisons] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [paymentModal, setPaymentModal] = useState({ isOpen: false, type: null });
  const [emailAddress, setEmailAddress] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);

  const handleEstimate = async (e) => {
    e.preventDefault();
    
    // Validate zip code is provided
    if (!zipCode || zipCode.trim().length !== 5) {
      toast.error('Please enter a valid 5-digit zip code');
      return;
    }
    
    setIsLoading(true);
    setEstimate(null);
    setComparisons(null);
    setShowComparison(false);

    try {
      // Determine if input is CPT code (numeric) or procedure name
      const isCPTCode = /^\d+$/.test(procedureInput.trim());
      const requestData = isCPTCode 
        ? { cpt_code: procedureInput.trim() }
        : { procedure_name: procedureInput.trim() };
      
      // Get cost estimate (may take longer due to Gemini API)
      const estimateResponse = await axios.post(`${API}/estimate`, requestData, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000  // 60 second timeout for Gemini API
      });

      setEstimate(estimateResponse.data);
      
      // Automatically fetch provider comparisons (no separate button needed)
      const comparisonRequestData = {
        ...requestData,
        user_zip: zipCode.trim()
      };
      
      toast.success('Getting your estimate and comparing providers...');
      
      const comparisonResponse = await axios.post(`${API}/compare-providers`, comparisonRequestData, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000  // 90 second timeout for 50-mile search
      });

      setComparisons(comparisonResponse.data);
      setShowComparison(true);
      toast.success(`Found ${comparisonResponse.data.length} providers within 50 miles - sorted by your lowest cost!`);
      
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to calculate estimate');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!zipCode) {
      toast.error('Please enter your zip code to compare providers');
      return;
    }

    setIsLoading(true);

    try {
      // Determine if input is CPT code (numeric) or procedure name
      const isCPTCode = /^\d+$/.test(procedureInput.trim());
      const requestData = {
        ...(isCPTCode ? { cpt_code: procedureInput.trim() } : { procedure_name: procedureInput.trim() }),
        user_zip: zipCode.trim()
      };
      
      const response = await axios.post(`${API}/compare-providers`, requestData, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000  // 90 second timeout for 50-mile search
      });

      setComparisons(response.data);
      setShowComparison(true);
      toast.success(`Found ${response.data.length} providers within 50 miles`);
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        toast.error('Search timed out. Try a smaller radius or a more common procedure.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to find nearby providers. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePDFExport = () => {
    setPaymentModal({ isOpen: true, type: 'pdf' });
  };

  const handlePDFDownload = async () => {
    try {
      const isCPTCode = /^\d+$/.test(procedureInput.trim());
      const requestData = {
        ...(isCPTCode ? { cpt_code: procedureInput.trim() } : { procedure_name: procedureInput.trim() }),
        user_zip: zipCode.trim()
      };

      const response = await axios.post(`${API}/generate-pdf`, requestData, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `procedure_estimate_${estimate.procedure_name.replace(/ /g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('PDF downloaded successfully!');
    } catch (error) {
      if (error.response?.status === 402) {
        handlePDFExport();
      } else {
        toast.error('Failed to generate PDF');
      }
    }
  };

  const handleEmailPrompt = () => {
    setShowEmailInput(true);
  };

  const handleEmailSend = async () => {
    if (!emailAddress || !emailAddress.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      const isCPTCode = /^\d+$/.test(procedureInput.trim());
      const requestData = {
        ...(isCPTCode ? { cpt_code: procedureInput.trim() } : { procedure_name: procedureInput.trim() }),
        user_zip: zipCode.trim()
      };

      await axios.post(
        `${API}/send-estimate-email`,
        requestData,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { recipient_email: emailAddress }
        }
      );

      toast.success(`Email sent to ${emailAddress}!`);
      setShowEmailInput(false);
      setEmailAddress('');
    } catch (error) {
      if (error.response?.status === 402) {
        setPaymentModal({ isOpen: true, type: 'email' });
      } else {
        toast.error('Failed to send email');
      }
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Search className="w-6 h-6" />
            Get Procedure Cost Estimate
          </CardTitle>
          <CardDescription>
            Enter a procedure name or CPT code to estimate your out-of-pocket costs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleEstimate} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="procedure">Procedure Name or CPT Code</Label>
                <Input
                  id="procedure"
                  data-testid="procedure-input"
                  placeholder="e.g., Knee Arthroscopy or 29881"
                  value={procedureInput}
                  onChange={(e) => setProcedureInput(e.target.value)}
                  required
                />
                <p className="text-xs text-gray-500">
                  Try: Knee Arthroscopy, Colonoscopy, Cataract Surgery, or CPT codes like 29881, 45378
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="zipcode">Your Zip Code *</Label>
                <Input
                  id="zipcode"
                  data-testid="zipcode-input"
                  placeholder="e.g., 10001"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  maxLength="5"
                  required
                />
                <p className="text-xs text-gray-500">
                  We'll automatically compare providers within 50 miles
                </p>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="calculate-estimate-button"
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 gap-2"
              disabled={isLoading}
            >
              <DollarSign className="w-4 h-4" />
              {isLoading ? 'Getting estimate & comparing providers...' : 'Get Estimate & Compare Providers'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Estimate Results Summary */}
      {estimate && (
        <Card className="border-0 shadow-xl bg-gradient-to-br from-green-50 to-emerald-50" data-testid="estimate-results">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              Your {insurance?.carrier_name} Average Cost Estimate
            </CardTitle>
            <CardDescription className="text-sm">
              {estimate.procedure_name} {estimate.cpt_code && `(CPT: ${estimate.cpt_code})`}
              <span className="block mt-1 text-green-700 font-medium text-xs">
                ✓ Based on average market rates • See provider-specific costs below
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Compact Cost Display */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center">
              <p className="text-xs text-gray-600 mb-1">Average Estimated Cost</p>
              <p className="text-3xl font-bold text-green-600" data-testid="patient-cost">
                ${estimate.estimated_patient_cost.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Insurance pays ~${estimate.insurance_pays.toFixed(2)} • You pay ~${estimate.estimated_patient_cost.toFixed(2)}
              </p>
              <p className="text-xs text-blue-600 mt-2 font-medium">
                📍 Scroll down to see EXACT costs at each provider near you
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* PDF and Email Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={handlePDFDownload}
                  variant="outline"
                  className="gap-2 border-2 hover:bg-blue-50"
                >
                  <FileText className="w-4 h-4" />
                  Export PDF
                </Button>
                <Button
                  onClick={handleEmailPrompt}
                  variant="outline"
                  className="gap-2 border-2 hover:bg-purple-50"
                >
                  <Mail className="w-4 h-4" />
                  Email Report
                </Button>
              </div>

              {/* Email Input */}
              {showEmailInput && (
                <div className="space-y-2 p-4 bg-white/60 backdrop-blur-sm rounded-xl">
                  <Label htmlFor="email">Recipient Email</Label>
                  <div className="flex gap-2">
                    <Input
                      id="email"
                      type="email"
                      placeholder="email@example.com"
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                    />
                    <Button onClick={handleEmailSend} className="bg-gradient-to-r from-purple-500 to-pink-500">
                      Send
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={paymentModal.isOpen}
        onClose={() => setPaymentModal({ isOpen: false, type: null })}
        paymentType={paymentModal.type}
        token={token}
        onSuccess={() => {
          // Retry the action after successful payment
          if (paymentModal.type === 'pdf') {
            handlePDFDownload();
          } else if (paymentModal.type === 'email') {
            handleEmailSend();
          }
        }}
      />

      {/* Provider Comparison */}
      {showComparison && comparisons && (
        <ComparisonView 
          comparisons={comparisons} 
          currentEstimate={estimate} 
          insuranceCarrier={insurance?.carrier_name}
        />
      )}
    </div>
  );
}